import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Copy,
  LayoutTemplate,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';


import { UnifiedListLayout } from "@/components/layout/unified-list-layout";
import { IntroCard } from '@/components/shared/IntroCard';
import { UnifiedSearch } from '@/components/shared/UnifiedSearch';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useCreateTemplateMutation, useDeleteTemplateMutation, useTemplatesQuery } from '@/hooks/useTemplateQueries';

// --- Types ---
interface TemplateItem {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string | null;
  is_public: boolean;
}

// --- Component ---
// Template Manager Page (Legacy Route) - updated to use new Table View
const TemplateManagerPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const { toast } = useToast();
  // const { hasAIFeatures } = useLicense(); // Removed unused


  // State
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isAddingTemplate, setIsAddingTemplate] = useState<boolean>(false);
  const [newTemplateName, setNewTemplateName] = useState<string>('');
  const [newTemplateDescription, setNewTemplateDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);


  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [templateToDelete, setTemplateToDelete] = useState<{ id: number; name: string } | null>(null);

  // Queries
  const projectIdNum = projectId ? parseInt(projectId) : undefined;
  const { data: templatesData, isLoading: isTemplatesLoading } = useTemplatesQuery(projectIdNum);

  // Settings
  // Use localStorage instead of project settings to avoid API calls
  const { value: isIntroDismissed, setValue: setIsIntroDismissed } = useLocalStorage<boolean>(`templates_intr_v2_${projectId}`, false);

  const dismissIntro = () => {
    setIsIntroDismissed(true);
  };

  // Mutations
  const createTemplateMutation = useCreateTemplateMutation();
  const deleteTemplateMutation = useDeleteTemplateMutation();

  // Filter templates
  const filteredTemplates = (templatesData || []).filter(template => {
    const term = searchQuery.toLowerCase();
    return (
      template.name.toLowerCase().includes(term) ||
      (template.description && template.description.toLowerCase().includes(term))
    );
  });

  // Actions
  const addTemplate = async () => {
    if (!newTemplateName.trim() || !projectId) return;

    try {
      setIsSubmitting(true);
      const createdTemplate = await createTemplateMutation.mutateAsync({
        name: newTemplateName.trim(),
        description: newTemplateDescription || undefined,
        is_public: true,
        project_id: parseInt(projectId)
      });

      setNewTemplateName('');
      setNewTemplateDescription('');
      setIsAddingTemplate(false);

      toast({
        title: "Template created",
        description: `Template "${createdTemplate.name}" created successfully.`,
      });

      navigate(`/projects/${projectId}/form-builder/editor?template=${createdTemplate.id}`);
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        title: "Error",
        description: "Failed to create template.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteTemplate = async () => {
    if (!projectId || !templateToDelete) return;
    try {
      await deleteTemplateMutation.mutateAsync({
        templateId: templateToDelete.id,
        projectId: parseInt(projectId)
      });
      toast({ title: "Template deleted", description: "The template has been permanently deleted." });
      setIsDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete template.", variant: "destructive" });
    }
  };

  const duplicateTemplate = async (template: TemplateItem) => {
    if (!projectId) return;
    try {
      await createTemplateMutation.mutateAsync({
        name: `${template.name} (Copy)`,
        description: template.description || undefined,
        is_public: template.is_public,
        project_id: parseInt(projectId)
      });
      toast({ title: "Template duplicated", description: `Copy of "${template.name}" created.` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to duplicate template.", variant: "destructive" });
    }
  };

  // --- Render ---
  return (
    <UnifiedListLayout
      toolbar={
        <>
          <UnifiedSearch
            placeholder="Search templates..."
            value={searchQuery}
            onChange={setSearchQuery}
            className="w-full max-w-sm"
          />
          <Button
            onClick={() => setIsAddingTemplate(true)}
            className="h-10 px-6 rounded-full shadow-lg shadow-primary/20"
          >
            <Plus className="mr-2 h-4 w-4" />
            New Template
          </Button>
        </>
      }
    >
      {/* Intro Card */}
      {!isIntroDismissed && !isTemplatesLoading && filteredTemplates.length === 0 && (
        <IntroCard
          title="Start with a Template"
          description="Templates define the structure of your content. Create custom forms for characters, locations, chapters, or any other story element."
          onClose={dismissIntro}
          className="mb-8"
          actions={
            <Button
              variant="outline"
              className="w-full justify-start text-xs h-9"
              onClick={() => setIsAddingTemplate(true)}
            >
              <Plus className="h-3 w-3 mr-2" />
              Create First Template
            </Button>
          }
        />
      )}

      <div className="grid grid-cols-1 gap-8">
        {/* Templates List */}
        <div>
          <Card className="bg-card border-border shadow-sm backdrop-blur-sm overflow-hidden rounded-2xl">
            <CardContent className="p-0">
              {isTemplatesLoading ? (
                <div className="space-y-4 py-8 px-6">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="flex items-center space-x-4 px-2">
                      <Skeleton className="h-5 w-5 rounded-full" />
                      <Skeleton className="h-5 w-[200px]" />
                      <Skeleton className="h-5 w-[100px] ml-auto" />
                    </div>
                  ))}
                </div>
              ) : filteredTemplates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  {searchQuery ? (
                    <>
                      <div className="bg-muted/60 rounded-full p-4 mb-5">
                        <Search className="h-10 w-10 text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-medium mb-3">No templates found</h3>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        No templates match your search query "{searchQuery}".
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="bg-primary/5 rounded-full p-4 mb-5">
                        <LayoutTemplate className="h-10 w-10 text-primary/70" />
                      </div>
                      <h3 className="text-xl font-medium mb-3">No templates yet</h3>
                      <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
                        Create your first template to start building forms.
                      </p>
                      <Button onClick={() => setIsAddingTemplate(true)} className="px-6 py-2 h-10 rounded-full shadow-lg shadow-primary/20">
                        <Plus className="mr-2 h-4 w-4" />
                        Create Template
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="w-full text-left text-sm">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border bg-muted/5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                    <div className="col-span-4">Template Name</div>
                    <div className="col-span-5">Description</div>
                    <div className="col-span-3 text-right">Last Modified</div>
                  </div>

                  <div className="divide-y divide-border">
                    <AnimatePresence initial={false}>
                      {filteredTemplates.map((template) => (
                        <motion.div
                          key={template.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          layout
                          className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-muted/5 transition-colors cursor-pointer group text-sm"
                          onClick={() => navigate(`/projects/${projectId}/form-builder/editor?template=${template.id}`)}
                        >
                          <div className="col-span-4 font-medium text-foreground flex items-center gap-3">
                            {template.name}
                          </div>
                          <div className="col-span-5 text-muted-foreground line-clamp-1 text-xs">
                            {template.description || '-'}
                          </div>
                          <div className="col-span-3 text-right flex items-center justify-end gap-3">
                            <span className="text-muted-foreground text-xs font-mono mr-2">
                              {template.updated_at
                                ? format(new Date(template.updated_at), 'MMM d, yyyy')
                                : format(new Date(template.created_at), 'MMM d, yyyy')
                              }
                            </span>

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 rounded-md p-0"
                                onClick={() => navigate(`/projects/${projectId}/form-builder/editor?template=${template.id}`)}
                              >
                                <Pencil className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 rounded-md p-0"
                                onClick={() => duplicateTemplate(template)}
                              >
                                <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 rounded-md p-0 hover:bg-destructive/10"
                                onClick={() => {
                                  setTemplateToDelete(template);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={isAddingTemplate} onOpenChange={setIsAddingTemplate}>
        <DialogContent className="sm:max-w-[600px] sm:rounded-2xl border-border/60 shadow-2xl p-0 overflow-hidden gap-0">
          <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
            <DialogTitle className="text-2xl font-semibold text-foreground tracking-tight">New Template</DialogTitle>
            <DialogDescription className="text-base text-muted-foreground leading-relaxed">
              Create a blank template to start building.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 px-8 py-8">
            <div className="space-y-2.5">
              <label className="text-sm font-medium text-foreground/80">Name</label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="e.g. Character Sheet"
                className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
                autoFocus
              />
            </div>
            <div className="space-y-2.5">
              <label className="text-sm font-medium text-foreground/80">Description</label>
              <Textarea
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="Optional description"
                className="min-h-[100px] resize-none rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
              />
            </div>
          </div>
          <DialogFooter className="p-6 bg-muted/5 border-t border-border/40">
            <Button
              variant="ghost"
              onClick={() => setIsAddingTemplate(false)}
              className="min-w-[100px] rounded-xl hover:bg-muted/50"
            >
              Cancel
            </Button>
            <Button
              onClick={addTemplate}
              disabled={!newTemplateName.trim() || isSubmitting}
              className="min-w-[120px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base"
            >
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[400px] bg-popover border-border text-popover-foreground">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete Template?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground pt-2">
              This will permanently delete <strong>{templateToDelete?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setIsDeleteDialogOpen(false)} className="hover:bg-muted/5">Cancel</Button>
            <Button variant="destructive" onClick={deleteTemplate}>Delete Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UnifiedListLayout>
  );
};

export default TemplateManagerPage;
